/**
 * PortfolioBoard: task-centric board view of the property portfolio.
 *
 * Columns are the thirteen Setup steps from `computeSetupSteps`, in
 * the same order as the property modal's Setup tab. A property card
 * appears in EVERY column where that step is still outstanding for
 * that property (multi-placement). Clearing the step in the modal
 * removes the card from that column on next refresh.
 *
 * Only columns that hold at least one card under the current status
 * filter are drawn, so the board collapses to just the work that
 * exists. Every step is still computed, so a column reappears the
 * moment a card lands in it, which is how a silent auto-step failure
 * surfaces. Cards for genuine machine-step failures (missing Charter
 * number, or an inconsistency warning from setup-steps) render as loud
 * alarm cards so they cannot sit unattended.
 *
 * The spinner shows on first load only; later refetches update the
 * board quietly in place rather than blanking it.
 *
 * Single source of truth: setup-steps.ts. If a step is renamed,
 * reordered, or its done-rule changes there, the board updates with
 * it automatically. No drift possible.
 *
 * Uses the canonical .ops-board / .ops-board-column / .ops-board-card
 * classes from global.css. The only inline style is the column's
 * data-driven top-border colour, which is the one exception in the
 * HARD STOP rule.
 */

import { useEffect, useMemo, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import AlertDialog, { type AlertTone } from '../../../../components/AlertDialog';
import { useToast } from '../../../../components/Toast';
import CharterDeliveryModal, { type CharterDeliveryTarget } from '../charters/CharterDeliveryModal';
import BoardLinkCustomerModal from './board-actions/BoardLinkCustomerModal';
import BoardLinkPersonModal from './board-actions/BoardLinkPersonModal';
import BoardSuiteInviteModal from './board-actions/BoardSuiteInviteModal';
import { computeSetupSteps, type SetupStepKey } from './property-detail/setup-steps';
import type { EnrichedProperty, PropertyMode, PropertyTab } from './property-detail/types';
import { computeTemperature } from './constants';

const TIER_INTERVAL_DAYS: Record<string, number> = {
  signature: 120,
  distinguished: 180,
  assured: 365,
};

function computeRecalcAssured(lastServiceDate: string | null, tier: string | null): string | null {
  if (!lastServiceDate) return null;
  const days = TIER_INTERVAL_DAYS[tier ?? 'assured'] ?? 365;
  const d = new Date(lastServiceDate + 'T00:00:00Z');
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function isAssuredCurrent(assuredUntil: string | null): boolean {
  if (!assuredUntil) return false;
  return new Date(assuredUntil).getTime() >= Date.now();
}

/** Contract-status values that the board filters on. Anything else is
 *  treated as "Onboarding" for filter purposes (defensive default).
 *
 *  'prospective' means the property exists and a proposal is with the client,
 *  but they have committed to nothing. Deliberately OUT of the default filter:
 *  those cards are not work anyone owes, the ball is with the client, and
 *  showing them as outstanding onboarding steps is exactly what made Kings
 *  Camp look neglected. They promote themselves to onboarding the moment the
 *  client accepts a proposal or a POS invoice is sent or paid. */
type ContractStatus = 'prospective' | 'onboarding' | 'active' | 'expired';
const STATUS_FILTER_STORAGE_KEY = 'posPortfolioStatusFilter';
const DEFAULT_STATUS_FILTER: ContractStatus[] = ['onboarding', 'active'];

function normaliseStatus(raw: string | null | undefined): ContractStatus {
  if (raw === 'prospective') return 'prospective';
  if (raw === 'active') return 'active';
  if (raw === 'expired') return 'expired';
  return 'onboarding';
}

function loadStatusFilter(): Set<ContractStatus> {
  if (typeof window === 'undefined') return new Set(DEFAULT_STATUS_FILTER);
  try {
    const raw = window.localStorage.getItem(STATUS_FILTER_STORAGE_KEY);
    if (!raw) return new Set(DEFAULT_STATUS_FILTER);
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set(DEFAULT_STATUS_FILTER);
    const valid = parsed.filter((v): v is ContractStatus =>
      v === 'prospective' || v === 'onboarding' || v === 'active' || v === 'expired');
    return new Set(valid.length > 0 ? valid : DEFAULT_STATUS_FILTER);
  } catch {
    return new Set(DEFAULT_STATUS_FILTER);
  }
}

interface PortfolioBoardProps {
  supabase: SupabaseClient;
  properties: Array<Record<string, unknown>>;
  openProperty: (id: string, initialTab?: PropertyTab, mode?: PropertyMode) => void;
  onRefresh: () => void | Promise<void>;
  /** Edit permission. View-level users see the cards but no action buttons. */
  canEdit: boolean;
}

/**
 * One card on the board. `alarm` is true only when a machine-driven step
 * that should have fired didn't (missing Charter number, or an explicit
 * inconsistency warning from setup-steps). Steps that are simply not yet
 * reached during normal onboarding stay quiet (alarm false).
 */
interface BoardCard {
  p: EnrichedProperty;
  alarm: boolean;
  alarmText: string | null;
  /** Set when this column's action can't be taken yet: names the earlier
   *  step to clear first. Shown as a calm lock note, disables the action. */
  blockedBy: string | null;
}

/**
 * Column top-border colours grouped by workflow phase:
 * red = foundation blockers (1-3), orange = setup (4-5),
 * gold = certification (6-9), green = activation (10-12),
 * grey = paperwork (13).
 */
const COLUMN_COLOUR: Record<SetupStepKey, string> = {
  customer:          'var(--color-error)',
  manager:           'var(--color-error)',
  ambassador:        'var(--color-error)',
  service:           'var(--color-warning)',
  cmb_certificate:   '#bd982f',
  rooms:             '#bd982f',
  charter_issued:    '#bd982f',
  certified_since:   '#bd982f',
  verify_page:       '#bd982f',
  suite_invite:      'var(--color-success)',
  charter_delivered: 'var(--color-success)',
  listed:            'var(--color-success)',
  agreement:         'var(--color-text-light)',
};

/**
 * Synthetic empty property used once at module load to derive the
 * column structure (key, label, tab) from setup-steps.ts. Avoids
 * hardcoding the column metadata here and drifting from the modal.
 */
const STUB_PROPERTY = {
  id: '', property_name: '', property_code: null, property_category: null,
  property_type: '', hospitality_tier: '', contract_status: '',
  charter_number: null, charter_issued_at: null, charter_delivered_at: null,
  charter_delivery_notes: null, charter_delivery_to: null, charter_delivery_address: null,
  charter_delivery_sent_by: null, charter_delivery_doc_url: null,
  assured_until: null, last_service_date: null,
  status: '', total_beds: null, total_rooms: null, slug: null, country: null,
  province: null, city: null, address_line1: null, address_line2: null,
  postal_code: null, contact_first_name: null, contact_last_name: null,
  contact_email: null, contact_phone: null, customer_id: null,
  ambassador_id: null, assigned_manager_id: null, created_at: null,
  is_published: null, is_featured: null, tagline: null, description: null,
  hero_image_url: null, brand_logo_url: null, gallery_images: null,
  amenity_tags: null, public_email: null, public_phone: null,
  whatsapp_number: null, listing_links: null, certified_since: null,
  external_rating: null, external_rating_source: null, external_review_count: null,
  proposal_id: null, group_id: null, customer: null, ambassador: null, manager: null,
} as EnrichedProperty;

const COLUMN_ORDER = computeSetupSteps(STUB_PROPERTY, {
  agreementOnFile: false, hasActiveCertificate: false, hasSuiteAccess: false, ambassadorDecided: false,
}).map((s) => ({
  key: s.key,
  label: s.shortLabel,
  tab: s.tab,
  color: COLUMN_COLOUR[s.key],
}));

export default function PortfolioBoard({ supabase, properties, openProperty, onRefresh, canEdit }: PortfolioBoardProps) {
  const { showToast } = useToast();
  const [agreementOnFile, setAgreementOnFile] = useState<Set<string>>(new Set());
  const [hasActiveCert, setHasActiveCert] = useState<Set<string>>(new Set());
  const [hasSuiteAccess, setHasSuiteAccess] = useState<Set<string>>(new Set());
  const [ambassadorDecided, setAmbassadorDecided] = useState<Set<string>>(new Set());
  // Spinner shows on the FIRST load only. Later refetches (after an
  // action) update the board quietly in place so it never blanks out.
  const [firstLoad, setFirstLoad] = useState(true);
  const [statusFilter, setStatusFilter] = useState<Set<ContractStatus>>(() => loadStatusFilter());

  // Per-column quick-action modal targets. Only one is open at a time
  // — opening another clears any others, set null to close.
  const [linkCustomerTarget, setLinkCustomerTarget] = useState<{ id: string; name: string } | null>(null);
  const [linkManagerTarget, setLinkManagerTarget] = useState<{ id: string; name: string; current: string | null } | null>(null);
  const [linkAmbassadorTarget, setLinkAmbassadorTarget] = useState<{ id: string; name: string; current: string | null } | null>(null);
  const [suiteInviteTarget, setSuiteInviteTarget] = useState<{ id: string; name: string; contactEmail: string | null; contactFirstName: string | null; contactLastName: string | null } | null>(null);
  const [deliveryTarget, setDeliveryTarget] = useState<CharterDeliveryTarget | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Branded blocking-validation dialog. Replaces the bare error toasts
  // for "you can't do this yet because X" with a clear reason + next step.
  const [alert, setAlert] = useState<{
    tone: AlertTone;
    title: string;
    message: string;
    actionLabel?: string;
    onAction?: () => void;
  } | null>(null);

  // Persist filter selection so it survives reloads. localStorage write
  // is best-effort; if the browser blocks it we just lose the persistence.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STATUS_FILTER_STORAGE_KEY, JSON.stringify(Array.from(statusFilter)));
    } catch {
      // ignore
    }
  }, [statusFilter]);

  function toggleStatus(s: ContractStatus) {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  }

  // The exception board holds onboarding work: new properties being set up
  // AND healthy certified properties that still have setup steps outstanding
  // (e.g. Suite invite not sent, Charter not delivered). It excludes only the
  // renewal cases — a property in the expiry window (temperature yellow /
  // orange / red) or already being worked as a renewal lives on the renewals
  // board, so expiry never clutters this one.
  const onboardingProperties = useMemo(
    () => properties.filter((raw) => {
      const r = raw as { assured_until?: string | null; renewal_stage?: string | null };
      const temp = computeTemperature(r.assured_until ?? null);
      const inRenewal = temp === 'yellow' || temp === 'orange' || temp === 'red' || !!r.renewal_stage;
      return !inRenewal;
    }),
    [properties],
  );

  // Per-status counts across the onboarding population, ignoring the current
  // filter, so the pill labels stay accurate as you toggle.
  const statusCounts = useMemo(() => {
    const counts: Record<ContractStatus, number> = { prospective: 0, onboarding: 0, active: 0, expired: 0 };
    for (const raw of onboardingProperties) {
      const cs = normaliseStatus((raw as { contract_status?: string | null }).contract_status);
      counts[cs] += 1;
    }
    return counts;
  }, [onboardingProperties]);

  // Only never-certified properties whose contract_status is in the active
  // filter set are passed through to the bucketing logic below.
  const filteredProperties = useMemo(() => {
    return onboardingProperties.filter((raw) => {
      const cs = normaliseStatus((raw as { contract_status?: string | null }).contract_status);
      return statusFilter.has(cs);
    });
  }, [onboardingProperties, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (properties.length === 0) {
        setAgreementOnFile(new Set());
        setHasActiveCert(new Set());
        setHasSuiteAccess(new Set());
        setAmbassadorDecided(new Set());
        setFirstLoad(false);
        return;
      }
      const propertyIds = properties.map((p) => p.id as string).filter(Boolean);
      const groupIds = Array.from(new Set(
        properties
          .map((p) => (p as { group_id?: string | null }).group_id)
          .filter((g): g is string => !!g),
      ));

      // Agreement = either legacy PDF on the property row OR an
      // auto-confirmed property_agreements row from the invoice T&C path.
      const propsWithPdf = new Set(
        properties
          .filter((p) => !!(p as { agreement_pdf_url?: string | null }).agreement_pdf_url)
          .map((p) => p.id as string),
      );

      const [agreementRes, certRes, suiteInviteRes, propMgrRes, groupMgrRes, decidedRes] = await Promise.all([
        supabase.from('property_agreements').select('property_id').in('property_id', propertyIds),
        supabase.from('cert_certificates').select('property_id').in('property_id', propertyIds).eq('certificate_status', 'valid'),
        supabase.from('charter_suite_invites').select('property_id').in('property_id', propertyIds),
        supabase.from('hosp_property_managers').select('property_id').in('property_id', propertyIds),
        groupIds.length > 0
          ? supabase.from('hosp_group_managers').select('group_id').in('group_id', groupIds)
          : Promise.resolve({ data: [] as Array<{ group_id: string }> }),
        supabase.from('hosp_properties').select('id, ambassador_decided').in('id', propertyIds).eq('ambassador_decided', true),
      ]);

      if (cancelled) return;

      const agreementSet = new Set(propsWithPdf);
      ((agreementRes.data ?? []) as Array<{ property_id: string }>)
        .forEach((r) => { if (r.property_id) agreementSet.add(r.property_id); });

      const certSet = new Set(
        ((certRes.data ?? []) as Array<{ property_id: string }>)
          .map((r) => r.property_id)
          .filter(Boolean),
      );

      const suiteSet = new Set<string>();
      ((suiteInviteRes.data ?? []) as Array<{ property_id: string }>)
        .forEach((r) => { if (r.property_id) suiteSet.add(r.property_id); });
      ((propMgrRes.data ?? []) as Array<{ property_id: string }>)
        .forEach((r) => { if (r.property_id) suiteSet.add(r.property_id); });
      const groupsWithManager = new Set(
        ((groupMgrRes.data ?? []) as Array<{ group_id: string }>)
          .map((r) => r.group_id)
          .filter(Boolean),
      );
      properties.forEach((p) => {
        const gid = (p as { group_id?: string | null }).group_id;
        if (gid && groupsWithManager.has(gid)) suiteSet.add(p.id as string);
      });

      const decidedSet = new Set(
        ((decidedRes.data ?? []) as Array<{ id: string }>)
          .map((r) => r.id)
          .filter(Boolean),
      );

      setAgreementOnFile(agreementSet);
      setHasActiveCert(certSet);
      setHasSuiteAccess(suiteSet);
      setAmbassadorDecided(decidedSet);
      setFirstLoad(false);
    }
    void load();
    return () => { cancelled = true; };
  }, [supabase, properties]);

  // Per-property step computation, same source of truth as the modal.
  // Only properties matching the active status filter contribute cards.
  const outstandingByColumn = useMemo<Record<SetupStepKey, BoardCard[]>>(() => {
    const empty = Object.fromEntries(
      COLUMN_ORDER.map((c) => [c.key, [] as BoardCard[]]),
    ) as Record<SetupStepKey, BoardCard[]>;

    for (const raw of filteredProperties) {
      const p = raw as unknown as EnrichedProperty;
      const steps = computeSetupSteps(p, {
        agreementOnFile: agreementOnFile.has(p.id),
        hasActiveCertificate: hasActiveCert.has(p.id),
        hasSuiteAccess: hasSuiteAccess.has(p.id),
        ambassadorDecided: ambassadorDecided.has(p.id),
      });
      for (const step of steps) {
        if (step.done || step.disabled) continue;
        // A card shouts only when a machine step that should have fired
        // didn't: a missing Charter number (the trigger always stamps it,
        // so its absence is always an anomaly), or an explicit
        // inconsistency warning from setup-steps (e.g. service on file but
        // assurance dates never derived). A step that is simply not yet
        // reached during normal onboarding carries no warning and stays
        // quiet — that's how we avoid crying wolf on every onboarding row.
        const alarmText = step.key === 'charter_issued'
          ? 'Charter number never stamped. The system sets this automatically, so this needs a look.'
          : step.warning ?? null;
        empty[step.key].push({ p, alarm: !!alarmText, alarmText, blockedBy: step.blockedBy ?? null });
      }
    }
    return empty;
  }, [filteredProperties, agreementOnFile, hasActiveCert, hasSuiteAccess, ambassadorDecided]);

  async function handleRecalcAssured(p: EnrichedProperty) {
    if (busyId) return;
    const recalc = computeRecalcAssured(p.last_service_date, p.hospitality_tier);
    if (!recalc) {
      setAlert({
        tone: 'warning',
        title: "Can't recalculate assurance yet",
        message: `${p.property_name} has no service logged, so there's no date to work the assurance period out from. Log a service first, then recalculate.`,
        actionLabel: 'Log a service',
        onAction: () => { setAlert(null); openProperty(p.id, 'records', 'edit'); },
      });
      return;
    }
    setBusyId(p.id);
    // Write both dates. If certified_since is already on file leave it,
    // otherwise stamp it from the property's last service date so the
    // Setup step closes (it requires BOTH values). Mirrors the trigger
    // that handles new service logs.
    const update: { assured_until: string; certified_since?: string } = { assured_until: recalc };
    if (!p.certified_since && p.last_service_date) {
      update.certified_since = p.last_service_date;
    }
    const { error } = await supabase.from('hosp_properties').update(update).eq('id', p.id);
    setBusyId(null);
    if (error) {
      showToast(error.message, 'error');
      return;
    }
    showToast(`Assurance dates set for ${p.property_name}.`, 'success');
    await onRefresh();
  }

  async function handleMarkListed(p: EnrichedProperty) {
    if (busyId) return;
    if (!isAssuredCurrent(p.assured_until)) {
      setAlert({
        tone: 'warning',
        title: "Can't list this property yet",
        message: `${p.property_name} needs a current Assured Until date before it can go on the public directory. The date is missing or has passed. Recalculate assurance first.`,
        actionLabel: 'Recalculate assurance',
        onAction: () => { setAlert(null); void handleRecalcAssured(p); },
      });
      return;
    }
    setBusyId(p.id);
    const { error } = await supabase.from('hosp_properties').update({ is_published: true }).eq('id', p.id);
    setBusyId(null);
    if (error) {
      showToast(error.message, 'error');
      return;
    }
    showToast(`${p.property_name} listed on directory.`, 'success');
    await onRefresh();
  }

  function openVerifyPage(p: EnrichedProperty) {
    if (!p.charter_number) {
      setAlert({
        tone: 'info',
        title: "Verify page isn't live yet",
        message: `${p.property_name} doesn't have a Charter number, so its public Verify page hasn't been generated. The page goes live automatically once the property is Active.`,
      });
      return;
    }
    const cc = (p.country || 'za').toLowerCase();
    const url = `https://peaceofsleep.vip/${cc}/verify/${encodeURIComponent(p.charter_number)}/`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /** Per-column quick-action descriptor. `label` shows on the button,
   *  `onClick` runs the action, `disabled` reads "Auto" rows etc. */
  function actionForColumn(key: SetupStepKey, p: EnrichedProperty, fallbackTab: PropertyTab | null): {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  } {
    switch (key) {
      case 'customer':
        return { label: 'Link customer', onClick: () => setLinkCustomerTarget({ id: p.id, name: p.property_name }) };
      case 'manager':
        return { label: 'Pick manager', onClick: () => setLinkManagerTarget({ id: p.id, name: p.property_name, current: p.assigned_manager_id }) };
      case 'ambassador':
        return { label: 'Pick ambassador', onClick: () => setLinkAmbassadorTarget({ id: p.id, name: p.property_name, current: p.ambassador_id }) };
      case 'rooms':
        return { label: 'Open Rooms', onClick: () => openProperty(p.id, 'rooms', 'edit') };
      case 'service':
        return { label: 'Link service log / invoice', onClick: () => openProperty(p.id, 'records', 'edit') };
      case 'cmb_certificate':
        return { label: 'Open Records', onClick: () => openProperty(p.id, 'records', 'edit') };
      case 'charter_issued':
        return { label: 'Auto', onClick: () => undefined, disabled: true };
      case 'certified_since':
        return {
          label: busyId === p.id ? 'Recalculating…' : 'Recalculate',
          onClick: () => { void handleRecalcAssured(p); },
          disabled: busyId === p.id,
        };
      case 'verify_page':
        return { label: 'Open page', onClick: () => openVerifyPage(p) };
      case 'suite_invite':
        return { label: 'Send invite', onClick: () => setSuiteInviteTarget({ id: p.id, name: p.property_name, contactEmail: (p.contact_email as string | null) ?? null, contactFirstName: (p.contact_first_name as string | null) ?? null, contactLastName: (p.contact_last_name as string | null) ?? null }) };
      case 'charter_delivered':
        return {
          label: 'Mark posted',
          onClick: () => setDeliveryTarget({
            id: p.id,
            property_name: p.property_name,
            charter_number: p.charter_number,
            charter_issued_at: p.charter_issued_at,
            charter_delivered_at: p.charter_delivered_at,
            charter_delivery_notes: p.charter_delivery_notes,
            charter_delivery_to: p.charter_delivery_to,
            charter_delivery_address: p.charter_delivery_address,
            charter_delivery_sent_by: p.charter_delivery_sent_by,
            charter_delivery_doc_url: p.charter_delivery_doc_url,
            address_line1: p.address_line1,
            address_line2: p.address_line2,
            city: p.city,
            province: p.province,
            postal_code: p.postal_code,
            contact_first_name: p.contact_first_name,
            contact_last_name: p.contact_last_name,
          }),
        };
      case 'listed':
        return {
          label: busyId === p.id ? 'Listing…' : 'Mark listed',
          onClick: () => { void handleMarkListed(p); },
          disabled: busyId === p.id,
        };
      case 'agreement':
        return { label: 'Open Records', onClick: () => openProperty(p.id, 'records', 'edit') };
      default:
        return { label: 'Open', onClick: () => openProperty(p.id, fallbackTab ?? 'setup', 'view') };
    }
  }

  if (firstLoad) return <LoadingSpinner />;

  const PILLS: Array<{ key: ContractStatus; label: string }> = [
    { key: 'prospective', label: `Prospective (${statusCounts.prospective})` },
    { key: 'onboarding', label: `Onboarding (${statusCounts.onboarding})` },
    { key: 'active',     label: `Active (${statusCounts.active})` },
    { key: 'expired',    label: `Expired (${statusCounts.expired})` },
  ];

  // Only draw columns that actually hold a card under the current filter.
  // Empty columns stay hidden, which quietens the board to just the work
  // that exists. Every one of the thirteen steps is still computed above,
  // so a column reappears the moment a card lands in it — including a
  // silent auto-step failure that would otherwise sit unseen.
  const visibleColumns = COLUMN_ORDER.filter((col) => outstandingByColumn[col.key].length > 0);

  return (
    <>
      <div className="flex items-center gap-sm flex-wrap mb-md">
        {PILLS.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => toggleStatus(p.key)}
            className={`toggle-btn ${statusFilter.has(p.key) ? 'toggle-btn--pos-active' : ''}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {visibleColumns.length === 0 ? (
        <div className="ops-board-empty">All caught up. No outstanding steps for the selected filter.</div>
      ) : (
        <div className="ops-board">
          {visibleColumns.map((col) => {
            const cards = outstandingByColumn[col.key];
            return (
              <div key={col.key} className="ops-board-column">
                <div className="ops-board-column-header" style={{ borderTopColor: col.color }}>
                  <div className="ops-board-column-title-group">
                    <span className="ops-board-column-title">{col.label}</span>
                    <span className="ops-board-column-count">{cards.length}</span>
                  </div>
                </div>
                <div className="ops-board-column-body">
                  {cards.map((card) => {
                    const p = card.p;
                    const cs = normaliseStatus(p.contract_status);
                    const act = actionForColumn(col.key, p, col.tab);
                    return (
                      <div
                        key={p.id}
                        className={`ops-board-card ${card.alarm ? 'ops-board-card--alarm' : `ops-board-card--${cs}`}`}
                        onClick={() => openProperty(p.id)}
                      >
                        {card.alarm ? (
                          <div className="ops-board-card-header">
                            <div className="ops-board-card-customer">{p.property_name}</div>
                            <span className="ops-board-card-alarm-icon" aria-label="Needs attention" title="Needs attention">!</span>
                          </div>
                        ) : (
                          <div className="ops-board-card-customer">{p.property_name}</div>
                        )}
                        {card.alarm && card.alarmText && (
                          <div className="ops-board-card-alarm-text">{card.alarmText}</div>
                        )}
                        {!card.alarm && card.blockedBy && (
                          <div className="ops-board-card-blocked">
                            <span className="ops-board-card-blocked-icon" aria-hidden="true">🔒</span>
                            <span className="ops-board-card-blocked-text">{card.blockedBy}</span>
                          </div>
                        )}
                        {canEdit && (
                        <div className="ops-board-card-actions ops-board-card-actions--mini">
                          <button
                            type="button"
                            className={`ops-board-action ops-board-action--sm${card.blockedBy ? ' ops-board-action--blocked' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              act.onClick();
                            }}
                            disabled={act.disabled || !!card.blockedBy}
                          >
                            {act.label}
                          </button>
                        </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {linkCustomerTarget && (
        <BoardLinkCustomerModal
          supabase={supabase}
          propertyId={linkCustomerTarget.id}
          propertyName={linkCustomerTarget.name}
          onClose={() => setLinkCustomerTarget(null)}
          onSaved={onRefresh}
        />
      )}

      {linkManagerTarget && (
        <BoardLinkPersonModal
          supabase={supabase}
          propertyId={linkManagerTarget.id}
          propertyName={linkManagerTarget.name}
          kind="manager"
          currentValue={linkManagerTarget.current}
          onClose={() => setLinkManagerTarget(null)}
          onSaved={onRefresh}
        />
      )}

      {linkAmbassadorTarget && (
        <BoardLinkPersonModal
          supabase={supabase}
          propertyId={linkAmbassadorTarget.id}
          propertyName={linkAmbassadorTarget.name}
          kind="ambassador"
          currentValue={linkAmbassadorTarget.current}
          onClose={() => setLinkAmbassadorTarget(null)}
          onSaved={onRefresh}
        />
      )}

      {suiteInviteTarget && (
        <BoardSuiteInviteModal
          supabase={supabase}
          propertyId={suiteInviteTarget.id}
          propertyName={suiteInviteTarget.name}
          contactEmail={suiteInviteTarget.contactEmail}
          contactFirstName={suiteInviteTarget.contactFirstName}
          contactLastName={suiteInviteTarget.contactLastName}
          onClose={() => setSuiteInviteTarget(null)}
          onSaved={onRefresh}
        />
      )}

      {deliveryTarget && (
        <CharterDeliveryModal
          supabase={supabase}
          property={deliveryTarget}
          canMark
          onClose={() => setDeliveryTarget(null)}
          onSaved={onRefresh}
        />
      )}

      {alert && (
        <AlertDialog
          tone={alert.tone}
          title={alert.title}
          message={alert.message}
          actionLabel={alert.actionLabel}
          onAction={alert.onAction}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
}
